
import Vue, { CreateElement } from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { Location, RawLocation } from 'vue-router';
import serverContextService from '@/core/serverContext.service';

@Component
export default class CultureAwareRouterLink extends Vue {
    @Prop({ type: [Object, String], default: '' }) to!: RawLocation;
    @Prop({ type: String }) target!: string;

    render(h: CreateElement) {
        // If href / to is missing, render plain <DIV>
        if (!this.to) {
            return h('div', {
                attrs: { ...this.$attrs }
            }, this.$slots.default);
        }
        const isExternalLink = isOutgoingUrl(this.to);
        const linkTag = isExternalLink ? 'a' : 'router-link';

        const attrs = {
            ...this.$attrs,
            ...(!isExternalLink && { to: fixupUrl(this.to), target: this.target }),
            ...(isExternalLink && { href: this.to, target: '_blank', rel: 'noopener' })
        };

        const listeners = isExternalLink
            ? {
                // For 'a' tag, combine normal and native listeners (not allowed to put native events (e.g. click.native) on 'a' tag)
                on: {
                    ...this.$listeners,
                    ...this.$vnode?.data?.nativeOn
                }
            } : {
                on: this.$listeners
            };

        return h(linkTag, {
            attrs: attrs,
            ...listeners
        }, this.$slots.default);

        function fixupUrl(to: RawLocation): RawLocation {
            let path = isLocation(to) ? to.path : to;
            let hash: string | undefined;
            if (path) {
                const { path: tmp, hash: tmpHash } = removeTrailingSlashAndLowercase(path);
                if (isFrontpagePath(tmp)) {
                    return '/' + (tmpHash || '');
                }

                path = hasCulturePrefix(tmp) ? withoutCulturePrefix(tmp) : tmp;
                hash = tmpHash;
            }

            if (!isLocation(to)) {
                return (path || '') + (hash || '');
            }

            const params = {
                ...to.params
            };
            for (const param in params) {
                params[param] = params[param].toLowerCase();
            }

            return {
                ...to,
                path,
                params,
                hash
            };

            function isFrontpagePath(to: string) {
                return to.endsWith(serverContextService.siteInfo.url!);
            }

            function hasCulturePrefix(to: string): boolean {
                return to.startsWith(serverContextService.siteInfo.url!);
            }

            function withoutCulturePrefix(url: string): string {
                return url.substr((serverContextService.siteInfo.url!.length));
            }

            function removeTrailingSlashAndLowercase(location: string): { path: string, hash?: string } {
                const [pathWithoutHash, hash] = location.split('#');
                const withoutTrailingSlash = pathWithoutHash.endsWith('/') && pathWithoutHash.length > 1 ? pathWithoutHash.slice(0, -1) : pathWithoutHash;
                // Lowercase path, but not query. We could have facets that are important to keep in original casing.
                let [path, query] = withoutTrailingSlash.split('?');
                path = path.toLowerCase().replaceAll(' ', '-').replaceAll('%20', '-');
                if (query) {
                    return {
                        path: path + '?' + query,
                        hash: hash ? '#' + hash : undefined
                    };
                }
                return {
                    path: path,
                    hash: hash ? '#' + hash : undefined
                };
            }
        }

        function isOutgoingUrl(to: RawLocation): boolean {
            const path = isLocation(to) ? to.path : to;
            if (!path) return false;

            let url;

            try {
                url = new URL(path);
            } catch (_) {
                return false;
            }

            return url.protocol === 'http:' || url.protocol === 'https:' || isExternalScheme(path);
        }

        function isLocation(to: RawLocation): to is Location {
            return typeof to === 'object';
        }

        // if a href points to external scheme e.g. (mailto:)
        function isExternalScheme(href: string): boolean {
            const externalScheme = RegExp('^(?:javascript|data|chrome|mailto|tel|sms|callto|mms|skype):.+$');
            return externalScheme.test(href);
        }
    }
}
